/**
 * S4b 멤버 프로필 (DESIGN_SPEC 7장, 프로토타입 isProfile).
 *
 *   상단 바(뒤로) → 아바타 96 → 호칭 → 역할·소개 → 한마디 → 정보 칩 → 푹 빠진 인물 → 살아온 이야기
 *   → 담당 콘텐츠 2열 → 이런 고민이면 나한테 → 고지 → 하단 고정 "○○와 1:1 대화"
 *
 * 뒤로 가면 들어온 곳(목록이나 1:1)으로 돌아갑니다. 바로 들어왔으면 목록입니다.
 */
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { TopBar } from '../../components/TopBar.jsx';
import { Avatar } from '../../components/Avatar.jsx';
import { Card } from '../../components/Card.jsx';
import { Button } from '../../components/Button.jsx';
import { useGoBack } from '../../hooks/useGoBack.js';

function SectionTitle({ children }) {
    return <h2 className="m-0 text-section text-label-muted">{children}</h2>;
}

export function MemberProfilePage() {
    const { memberKey } = useParams();
    const member = getMember(memberKey);
    const navigate = useNavigate();
    const goBack = useGoBack('/members');

    if (!member) {
        return <Navigate to="/members" replace />;
    }

    return (
        <div className="flex h-dvh flex-col">
            <TopBar title="멤버" onBack={goBack} bordered={false} />
            <main className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-gutter pt-2 pb-6">
                <div className="flex flex-col items-center gap-3 text-center">
                    <Avatar memberKey={memberKey} size={96} />
                    <div className="flex flex-col gap-1">
                        <h1 className="m-0 text-title-1">{member.name}</h1>
                        <span className="text-[13px] text-text-secondary">
                            {member.role} · {member.line}
                        </span>
                    </div>
                    <p className="m-0 font-serif text-title-2">{member.quote}</p>
                    <div className="flex flex-wrap justify-center gap-1.5">
                        {member.chips.map((chip) => (
                            <span
                                key={chip}
                                className="inline-flex h-7 items-center rounded-chip bg-surface-sunken px-2.5 text-[13px]"
                            >
                                {chip}
                            </span>
                        ))}
                    </div>
                </div>

                <section className="flex flex-col gap-2">
                    <SectionTitle>푹 빠진 인물</SectionTitle>
                    <Card className="flex flex-col gap-1">
                        <span className="text-body font-semibold">{member.figure}</span>
                        <span className="text-label leading-[22px] font-normal text-text-secondary">
                            {member.figureDescription}
                        </span>
                        <span className="mt-2 border-t border-hairline pt-2.5 text-[13px] leading-5 text-text-secondary">
                            {member.basis}
                        </span>
                    </Card>
                </section>

                <section className="flex flex-col gap-2">
                    <SectionTitle>살아온 이야기</SectionTitle>
                    <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
                        {member.story.map((line) => (
                            <li key={line} className="flex gap-2.5 text-[15px] leading-6">
                                <span
                                    aria-hidden="true"
                                    className="mt-[9px] size-1.5 shrink-0 rounded-full"
                                    style={{ background: `var(--color-m-${memberKey})` }}
                                />
                                {line}
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="flex flex-col gap-2">
                    <SectionTitle>담당 콘텐츠</SectionTitle>
                    <div className="grid grid-cols-2 gap-2">
                        {member.dailyContents.map((content) => (
                            <Card key={content.name} className="flex flex-col gap-0.5 px-3.5 py-3">
                                <span className="text-[15px] font-semibold">{content.name}</span>
                                <span className="text-[13px] text-text-secondary">{content.schedule}</span>
                            </Card>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-2">
                    <SectionTitle>이런 고민이면 나한테</SectionTitle>
                    <div className="flex flex-wrap gap-1.5">
                        {member.topics.map((topic) => (
                            <span
                                key={topic}
                                className="inline-flex h-8 items-center rounded-card border border-hairline bg-surface px-3 text-label font-normal"
                            >
                                {topic}
                            </span>
                        ))}
                    </div>
                </section>

                <span className="text-center text-[13px] text-text-secondary">
                    {withJosa(member.name, '은', '는')} AI가 연기하는 가상 인물이에요.
                </span>
            </main>
            <div className="shrink-0 border-t border-hairline bg-bg px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
                <Button fullWidth onClick={() => navigate(`/chat/${memberKey}`, { state: { from: 'profile' } })}>
                    {withJosa(member.name, '과', '와')} 1:1 대화
                </Button>
            </div>
        </div>
    );
}
